// services/leadService.ts

import { Lead, LeadStatus } from "../../interfaces/DTO/crm/lead";
import { LeadPortalLinkResponse } from "../../interfaces/DTO/public/leadPortal";
import { BaseService } from "../BaseService";
import apiClient from "../../core/apiClient";

const API_URL = "/leads";

class LeadService extends BaseService<Lead> {
  constructor() {
    super(API_URL);
  }

  /**
   * שליפת כל הלידים לפי סטטוס
   */
  async getByStatus(status: LeadStatus): Promise<Lead[]> {
    const response = await apiClient.get<Lead[]>(`${this.baseUrl}/by-status`, {
      params: { status }
    });
    return response.data;
  }

  /**
   * שליפת כל הלידים לפי סוכן משוייך
   */
  async getByAgent(agentId: number): Promise<Lead[]> {
    const response = await apiClient.get<Lead[]>(`${this.baseUrl}/by-agent`, {
      params: { agent_id: agentId }
    });
    return response.data;
  }

  /**
   * שליפת כל הלידים לפי קמפיין
   */
  async getByCampaign(campaignId: number): Promise<Lead[]> {
    const response = await apiClient.get<Lead[]>(`${this.baseUrl}/by-campaign`, {
      params: { campaign_id: campaignId }
    });
    return response.data;
  }

  /**
   * חיפוש ליד לפי מספר טלפון
   */
  async getByPhone(phone: string): Promise<Lead | null> {
    const response = await apiClient.get<Lead | null>(`${this.baseUrl}/by-phone`, {
      params: { phone }
    });
    return response.data;
  }

  /**
   * חיפוש ליד לפי כתובת אימייל
   */
  async getByEmail(email: string): Promise<Lead | null> {
    const response = await apiClient.get<Lead | null>(`${this.baseUrl}/by-email`, {
      params: { email }
    });
    return response.data;
  }

  /**
   * Workflow: הלקוח רוצה להתקדם
   * יוצר Party (DRAFT) ומעדכן Lead.status = IN_FLOW
   */
  async startFlow(leadId: number): Promise<Lead> {
    const response = await apiClient.post<Lead>(`${this.baseUrl}/${leadId}/start-flow`);
    return response.data;
  }

  /**
   * Workflow: הלקוח רק בירר
   * מעדכן Lead.notes עם הערה
   */
  async markAsInquired(leadId: number, notes?: string): Promise<Lead> {
    const response = await apiClient.post<Lead>(`${this.baseUrl}/${leadId}/mark-inquired`, null, {
      params: { notes }
    });
    return response.data;
  }

  /**
   * Workflow: בחירת חבילה (מסך 4/5)
   * ממיר Lead → Contact, יוצר Party, PartyParticipant, ו-Deal
   */
  /**
   * יצירת לינק ציבורי ל-Lead Portal
   * מחזיר: token, link (url מלא), expires_at
   */
  async getPortalLink(leadId: number): Promise<LeadPortalLinkResponse | null> {
    const response = await apiClient.get<LeadPortalLinkResponse | null>(
      `${this.baseUrl}/${leadId}/portal-link`
    );
    return response.data;
  }

  async createPortalLink(leadId: number, expiryDays = 3): Promise<LeadPortalLinkResponse> {
    const response = await apiClient.post<LeadPortalLinkResponse>(
      `${this.baseUrl}/${leadId}/portal-link`,
      null,
      { params: { expiry_days: expiryDays } }
    );
    return response.data;
  }

  async createDeal(leadId: number): Promise<{ deal_id: number; is_couple: boolean }> {
    const response = await apiClient.post<{ deal_id: number; is_couple: boolean }>(
      `${this.baseUrl}/${leadId}/create-deal`
    );
    return response.data;
  }

  async getConsentFiles(leadId: number): Promise<{ name: string; full_path: string }[]> {
    const response = await apiClient.get<{ name: string; full_path: string }[]>(
      `${this.baseUrl}/${leadId}/consent-files`
    );
    return response.data;
  }

  getConsentFileStreamUrl(leadId: number, fullPath: string): string {
    const base = process.env.REACT_APP_API_URL || "http://localhost:8000/api";
    return `${base}${this.baseUrl}/${leadId}/stream-consent-file?full_path=${encodeURIComponent(fullPath)}`;
  }

  async fixCopyFiles(leadId: number): Promise<{ lead_id: number; contact_id: number }> {
    const response = await apiClient.post(`${this.baseUrl}/${leadId}/fix-copy-files`);
    return response.data;
  }

  async bulkAssignCampaign(leadIds: number[], campaignId: number | null): Promise<{ updated: number }> {
    const response = await apiClient.patch<{ updated: number }>(
      `${this.baseUrl}/bulk-assign-campaign`,
      { lead_ids: leadIds, campaign_id: campaignId }
    );
    return response.data;
  }

  async selectPackage(
    leadId: number,
    pricingPlanId: number,
    partnerDetails?: {
      first_name: string;
      last_name: string;
      phone?: string;
      email?: string;
    }
  ): Promise<{
    deal_id: number;
    contact_id: number;
    party_id: number;
    next_step: string;
  }> {
    const response = await apiClient.post(
      `${this.baseUrl}/${leadId}/select-package`,
      partnerDetails || null,
      {
        params: { pricing_plan_id: pricingPlanId }
      }
    );
    return response.data;
  }
}

export const leadService = new LeadService();
